<template>
  <div
    ref="containerRef"
    :class="[
      'tm-auto-size',
      justify ? `tm-auto-size--justify-${justify}` : undefined,
      { 'tm-auto-size--multiline': isMultiline },
    ]"
    :style="{ maxWidth }"
  >
    <tm-auto-size-children-viewer
      hiding-mode="opacity0"
      :hidden-children-indexes="hiddenChildrenIndexes"
      @update-list-of-children="updateChildren"
    >
      <slot :visible-indexes="visibleChildrenIndexesWithoutMore" />
      <div class="tm-auto-size__more">
        <slot
          name="more"
          :more-visible-indexes="hiddenChildrenIndexes"
          :more-hidden-indexes="visibleChildrenIndexesWithoutMore"
        />
      </div>
    </tm-auto-size-children-viewer>
  </div>
</template>

<script lang="ts" setup>
/**
 * The component hides elements that do not fit in the container and allows you to move them to a more block.
 * It also allows you to change the hiding priority of elements by adding the special attribute to them.
 * See TmAutoSizePanel.vue for an example of how to use it
 */

import {
  computed,
  ref,
  useTemplateRef,
  watchEffect,
  useResizeObserver,
  useMutationObserver,
} from '@/composition/vue/compositionApi'
import {
  autoSize,
  type AutoSizeItem,
  type AutoSizeHidingPriority,
  autoSizeHidingPriorityAttributeName,
} from '@/composition/autoSize'
import TmAutoSizeChildrenViewer from '@/components/shared/autoSize/TmAutoSizeChildrenViewer.vue'

const props = defineProps<{
  // If true, the component will not grow beyond the maximum needed size to show all elements
  setMaxNeededWidth?: boolean
  minVisibleItems?: number
  maxVisibleItems?: number
  minHiddenItems?: number
  considerMoreAsVisibleItem?: boolean
  maxVisibleLines?: number
  justify?: 'start' | 'center' | 'end'
  isFreeze?: boolean
}>()

const containerRef = useTemplateRef('containerRef')

/* BASE DATA */

const containerWidth = ref(0)
// All elements in the content with more item
const children = ref<HTMLElement[]>([])
const childrenWidths = ref<number[]>([])
const childrenHidingPriorities = ref<AutoSizeHidingPriority[]>([])

/* COMPUTED HELPERS */

const moreItemIndex = computed(() => children.value.length - 1)
const moreItemWidth = computed(() => childrenWidths.value[moreItemIndex.value] ?? 0)
const isMultiline = computed(() => (props.maxVisibleLines ?? 1) > 1)

/* LOGIC */

const hiddenChildrenIndexes = ref<number[]>([])
const visibleChildrenIndexesWithoutMore = ref<number[]>([])
const maxNeededWidth = ref(0)

// Constraining the width before children are measured would collapse shrinkable ones to zero, and their measured width keeps maxNeededWidth at zero
const maxWidth = computed(() =>
  props.setMaxNeededWidth && childrenWidths.value.length > 0 ? `${maxNeededWidth.value}px` : undefined,
)

watchEffect(() => {
  if (props.isFreeze) {
    return
  }
  if (children.value.length === 0) {
    return
  }
  const childrenWithoutMoreItem = children.value.slice(0, moreItemIndex.value)
  const items: AutoSizeItem[] = childrenWithoutMoreItem.map((item, index) => ({
    element: item,
    width: childrenWidths.value[index],
    hidingPriority: childrenHidingPriorities.value[index],
  }))

  const result = autoSize({
    availableWidth: containerWidth.value,
    items,
    moreItemWidth: moreItemWidth.value,
    minVisibleItems: props.minVisibleItems,
    maxVisibleItems: props.maxVisibleItems,
    minHiddenItems: props.minHiddenItems,
    considerMoreAsVisibleItem: props.considerMoreAsVisibleItem,
    maxVisibleLines: props.maxVisibleLines,
  })

  // Update refs

  hiddenChildrenIndexes.value = result.isMoreVisible
    ? result.indexesToHide
    : [...result.indexesToHide, moreItemIndex.value]
  visibleChildrenIndexesWithoutMore.value = result.indexesToShow
  maxNeededWidth.value = result.maxNeededWidth
})

/* METHODS FOR UPDATING DATA */

const fetchContainerWidth = () => {
  containerWidth.value = containerRef.value?.getBoundingClientRect().width ?? 0
}

const updateChildWidth = (element: HTMLElement) => {
  const index = children.value.findIndex((e) => e === element)
  if (index !== -1) {
    childrenWidths.value[index] = element.getBoundingClientRect().width
  }
}

const fetchChildrenWidths = () => {
  childrenWidths.value = children.value.map((e) => e.getBoundingClientRect().width)
}

const getHidingPriority = (element: HTMLElement) => {
  const rawPriority = element.getAttribute(autoSizeHidingPriorityAttributeName)
  return rawPriority === 'alwaysVisible' || rawPriority === 'alwaysHidden' ? rawPriority : Number(rawPriority) || 0
}

const updateChildHidingPriority = (element: HTMLElement) => {
  const index = children.value.findIndex((e) => e === element)
  if (index !== -1) {
    childrenHidingPriorities.value[index] = getHidingPriority(element)
  }
}

const fetchChildrenHidingPriority = () => {
  childrenHidingPriorities.value = children.value.map((e) => getHidingPriority(e))
}

const updateChildren = (items: HTMLElement[]) => {
  children.value = items
  fetchChildrenWidths()
  fetchChildrenHidingPriority()
}

/* OBSERVERS */

// Update available width when container width changes
useResizeObserver(containerRef, fetchContainerWidth)

// Update children width when children width changes
useResizeObserver(
  () => children.value,
  (entries) => {
    entries.forEach((entry) => updateChildWidth(entry.target as HTMLElement))
  },
)

// Update children hiding priority when children attributes change
useMutationObserver(
  children,
  (mutations) => {
    const seen = new Set<HTMLElement>()
    for (const m of mutations) {
      const el = m.target as HTMLElement
      if (!seen.has(el)) {
        seen.add(el)
        updateChildHidingPriority(el)
      }
    }
  },
  {
    attributes: true,
    attributeFilter: [autoSizeHidingPriorityAttributeName],
  },
)

defineExpose({
  containerRef,
})
</script>

<style scoped lang="scss">
.tm-auto-size {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  position: relative;
  display: inline-flex;
  align-items: center;

  &--multiline {
    flex-wrap: wrap;
  }

  &--justify-start {
    justify-content: flex-start;
  }
  &--justify-center {
    justify-content: center;
  }
  &--justify-end {
    justify-content: flex-end;
  }
}
</style>
